import { supabase } from '../../common/config/supabase';
import { OrderDirection } from '../../common/enums/order-direction.enum';
import { RequestStatusEnum } from '../../common/enums/request-status.enum';
import { IPaginatedEntity } from '../../common/interfaces/paginated-entity.interface';
import { IRequest, IRequestDTO } from '../../common/interfaces/request.interface';

export const getRequests = async (
  company_id: string,
  client_id: string,
  limit: number,
  page: number,
  search?: string,
  orderBy?: string,
  orderDirection?: OrderDirection,
  status?: RequestStatusEnum,
): Promise<IPaginatedEntity<IRequest>> => {
  const { data, error } = await supabase.rpc('get_requests', {
    company_id_val: company_id,
    client_id_val: client_id,
    search_term: search || '',
    limit_val: limit,
    page_val: page,
    order_direction: orderDirection,
    order_by: orderBy,
    status_val: status,
  });

  if (error) {
    throw error;
  }

  return {
    items: data,
    meta: {
      currentPage: page,
      itemCount: data.length,
      itemsPerPage: limit,
      totalItems: data[0] ? data[0].total_count : 0,
      totalPages: data[0] ? Math.ceil(+data[0].total_count / limit) : 0,
      orderByColumn: orderBy,
      orderDirection: orderDirection,
    },
  };
};

export const getRequestsFilteredByYearAndMonth = async (
  year: string,
  month: string,
  limit: number,
  page: number,
  orderBy?: string,
  orderDirection?: OrderDirection,
): Promise<IPaginatedEntity<IRequest>> => {
  const { data } = await supabase.rpc('get_requests_filtered', {
    year_param: +year,
    month_param: +month,
    limit_val: limit,
    page_val: page,
    order_direction: orderDirection,
    order_by: orderBy,
  });

  return {
    items: data,
    meta: {
      currentPage: page,
      itemCount: data.length,
      itemsPerPage: limit,
      totalItems: data[0].total_count,
      totalPages: Math.ceil(+data[0].total_count / limit),
      orderByColumn: orderBy,
      orderDirection: orderDirection,
    },
  };
};

export const getRquestNextMonth = async (
  request_id: number,
): Promise<{ next_year: number; next_month: number } | null> => {
  const { data, error } = await supabase.rpc(
    'get_next_month_to_fulfill',
    {
      request_id_param: request_id,
    },
    { get: true },
  );

  if (error) {
    throw error;
  }

  if (!data || !data[0]) {
    return null;
  }

  return data[0];
};

export const addRequest = async (request: IRequestDTO): Promise<IRequest | null> => {
  const { data, error } = await supabase.from('requests').insert({
    name: request.name,
    alias: request.alias,
    cnp: request.cnp,
    start_date: request.start_date,
    end_date: request.end_date,
    details: request.details,
    client_id: request.client_id,
    company_id: request.company_id,
    birthplace: request.birthplace,
    working_periods: request.working_periods,
    job_titles: request.job_titles,
  });

  if (error) {
    throw error;
  }

  return data;
};

export const updateRequestStatus = async (request: {
  id: number;
  status: RequestStatusEnum;
}): Promise<IRequest | null> => {
  if (!request.id) {
    return null;
  }

  const { data, error } = await supabase
    .from('requests')
    .update({
      status: request.status,
    })
    .eq('id', request.id);

  if (error) {
    throw error;
  }

  return data;
};

export const updateRequest = async (request: IRequestDTO): Promise<IRequest | null> => {
  if (!request.id) {
    return null;
  }

  const { data, error } = await supabase
    .from('requests')
    .update({
      name: request.name,
      alias: request.alias,
      cnp: request.cnp,
      status: request.status,
      start_date: request.start_date,
      end_date: request.end_date,
      details: request.details,
      birthplace: request.birthplace,
      working_periods: request.working_periods,
      job_titles: request.job_titles,
    })
    .eq('id', request.id);

  if (error) {
    throw error;
  }

  return data;
};
